import React, { useState } from "react";
import {
  Button,
  TextField,
  Paper,
  Box,
  Container,
  Typography,
  Link,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LockResetIcon from "@mui/icons-material/LockReset";
import logo from "../assets/digifab-logo-siyah.png";
import { changePasswordRequired } from "../api/authApi";
import { setAccess, setMe } from "../redux/sessionSlice";

const theme = createTheme({
  palette: {
    primary: { main: "#6366f1" },
    secondary: { main: "#8b5cf6" },
  },
});

const passwordRules = Yup.string()
  .required("Şifre zorunludur")
  .min(8, "Şifre en az 8 karakter uzunluğunda olmalıdır")
  .test("has-uppercase", "En az bir büyük harf içermelidir", (value) =>
    /[A-Z]/.test(value || "")
  )
  .test(
    "has-special-char",
    "En az bir özel karakter içermelidir",
    (value) => /[@#$%^&*()\-._+=<>?/]/.test(value || "")
  );

const validationSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Mevcut şifre zorunludur"),
  password: passwordRules,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Şifreler eşleşmiyor")
    .required("Şifre doğrulama zorunludur"),
});

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="#" underline="hover">
        DigiFab
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

export default function FirstLoginPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accessToken = useSelector((s) => s.session.accessToken);
  const me = useSelector((s) => s.session.me);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    if (!accessToken) {
      toast.error("Oturum bulunamadı. Lütfen tekrar giriş yapın.");
      navigate("/", { replace: true });
      return;
    }

    try {
      setSubmitting(true);
      const data = await changePasswordRequired(accessToken, {
        current_password: values.currentPassword,
        new_password: values.password,
        confirm_new_password: values.confirmPassword,
      });

      dispatch(setAccess({ token: data.access_token, exp: data.exp }));
      dispatch(
        setMe({
          ...me,
          must_change_password: false,
        })
      );

      toast.success("Şifreniz güncellendi. Hoş geldiniz!");
      navigate("/home", { replace: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : detail?.message || "Şifre güncellenemedi";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              padding: { xs: 3, sm: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: 3,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="DigiFab Logo"
              sx={{ width: "50%", maxWidth: "220px", mb: 2 }}
            />
            <LockResetIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              İlk Giriş — Şifre Belirle
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
              Devam etmeden önce güvenliğiniz için yeni bir şifre belirlemeniz gerekiyor.
            </Typography>

            <Formik
              initialValues={{
                currentPassword: "",
                password: "",
                confirmPassword: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              <Form style={{ width: "100%" }}>
                <Field
                  as={TextField}
                  name="currentPassword"
                  label="Geçici / Mevcut Şifre"
                  type="password"
                  fullWidth
                  margin="normal"
                  autoFocus
                />
                <ErrorMessage name="currentPassword">
                  {(msg) => (
                    <Typography color="error" variant="caption">
                      {msg}
                    </Typography>
                  )}
                </ErrorMessage>

                <Field
                  as={TextField}
                  name="password"
                  label="Yeni Şifre"
                  type="password"
                  fullWidth
                  margin="normal"
                />
                <ErrorMessage name="password">
                  {(msg) => (
                    <Typography color="error" variant="caption">
                      {msg}
                    </Typography>
                  )}
                </ErrorMessage>

                <Field
                  as={TextField}
                  name="confirmPassword"
                  label="Yeni Şifre (Tekrar)"
                  type="password"
                  fullWidth
                  margin="normal"
                />
                <ErrorMessage name="confirmPassword">
                  {(msg) => (
                    <Typography color="error" variant="caption">
                      {msg}
                    </Typography>
                  )}
                </ErrorMessage>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={submitting}
                  sx={{ mt: 3, py: 1.5, textTransform: "none", fontWeight: 600 }}
                >
                  {submitting ? "Kaydediliyor..." : "Şifreyi Güncelle ve Devam Et"}
                </Button>
              </Form>
            </Formik>

            <Copyright sx={{ mt: 3 }} />
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
